import jsonpath from 'jsonpath';
import useSequence from '@/composables/useSequence';
import useConfig from '@/composables/useConfig';
import useMqtt from '@/composables/useMqtt';
import { VARIABLE_VALUE_TYPES } from '@/js/constants';

export default class IoTService {
  constructor() {
    this.sessionId = null;
    this.sessionHandlers = [];
  }

  async listen() {
    const { currentConfig } = useConfig();
    const { mqttSubscribe } = useMqtt();
    const promises = currentConfig.value.iot.map(({ topic, variables, next }) => {
      const handler = this.handlerFunction({ topic, variables, next });
      return mqttSubscribe({ scope: 'public', topic }, handler);
    });

    await Promise.all(promises);
  }

  async listenSession(sessionId = null) {
    const { currentConfig } = useConfig();
    const { mqttSubscribe, mqttUnsubscribe } = useMqtt();

    if (this.sessionId) {
      await Promise.all(
        currentConfig.value.iot.map(({ topic }, i) => mqttUnsubscribe(
          { scope: 'public', topic: `${topic}/session/${this.sessionId}` },
          this.sessionHandlers[i],
        )),
      );
    }

    this.sessionHandlers = [];

    if (sessionId) {
      await Promise.all(currentConfig.value.iot.map(({ topic, variables, next }) => {
        const handler = this.handlerFunction({ topic, variables, next });
        this.sessionHandlers.push(handler);
        return mqttSubscribe({ scope: 'public', topic: `${topic}/session/${sessionId}` }, handler);
      }));
    }

    this.sessionId = sessionId;
  }

  // eslint-disable-next-line class-methods-use-this
  handlerFunction({ topic, variables, next }) {
    const { setVariables, setFlowStateBackToStart } = useConfig();
    const { interruptSequence, runSequence } = useSequence();

    const func = (err, { message }) => {
      const data = JSON.parse(message);
      const variablesToSet = variables.reduce((res, { id, parameter }) => {
        const value = ['$[', '$.'].some((path) => parameter.startsWith(path)) || parameter === '$'
          ? jsonpath.value(data, parameter)
          : data[parameter];

        if (value !== undefined) {
          res.push({ id, value: { value, type: VARIABLE_VALUE_TYPES.PRIMITIVE } });
        }
        return res;
      }, []);

      interruptSequence();
      setVariables(variablesToSet);
      setFlowStateBackToStart(next);
      runSequence(next);
    };

    // we need it to handle processing and unsubscribe logic
    Object.defineProperty(func, 'name', { value: topic, writable: false });
    return func;
  }
}
